import React, { useState } from 'react'
import { useParams, useHistory, useRouteMatch } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { Create } from '@mui/icons-material'

import { Header } from '@trivie/ui-web/components/header'
import { NavigationTabs } from '@trivie/ui-web/components/navigation-tabs'
import { EditUser } from '@trivie/ui-web/components/edit-user'
import { useStores } from '@trivie/core'
import { useConfirm } from '@trivie/ui-web/components/dialogs/confirmation-dialog'

import { usersTabs } from './tabs'
import { Dialog } from '@trivie/ui-web/components/dialogs/dialog'
import { DateTime } from 'luxon'
import { SubscriptionUserPurchasesTab } from '../../activities/subscriptions/tabs'
import { UsersAssignmentsTab } from './tabs/users-assignments-tab'
import { UsersOverviewTab } from './tabs/users-overview-tab'
import { UsersQuestionsTab } from './tabs/users-questions-tab'
import { UsersTopicsTab } from './tabs/users-topics-tab'

export const UsersDetailsScreen: React.FunctionComponent = observer(() => {
  const { manageUserStore } = useStores()
  const {
    selectedUserId,
    getUserOverviewStats,
    getSelectedUserProfile,
    selectedUserProfile,
    deleteUsers,
    permissions,
    overviewStats,
    changeStatus,
  } = manageUserStore
  const [showEdit, setShowEdit] = useState(false)
  const [showUpdateDialog, setShowUpdateDialog] = useState(false)
  const [showConfirmDelete, setShowConfirmDelete] = useState(false)

  const [start, setStart] = useState<any>(DateTime.local(1971).startOf('day').toISO())
  const [end, setEnd] = useState<any>(DateTime.local().endOf('day').toISO())

  const confirm = useConfirm()

  const match = useRouteMatch<any>('/people/users/:id/overview')

  // @ts-ignore
  const { id } = useParams()
  const history = useHistory()

  const handleDelete = async () => {
    if (!selectedUserId) {
      return
    }
    await deleteUsers([String(selectedUserId)], () => {
      history.goBack()
    })
  }

  const primary = {
    disabled: !permissions?.permissions.some((p) => p.code === 'can_edit_user'),
    startIcon: <Create />,
    onClick: () => setShowEdit(true),
    text: 'Edit User',
  }

  return (
    <>
      <Header
        primaryAction={primary}
        title={
          selectedUserProfile && selectedUserProfile?.first_name && selectedUserProfile?.last_name
            ? `${selectedUserProfile?.first_name} ${selectedUserProfile?.last_name}`
            : selectedUserProfile?.email || ''
        }
      />
      <NavigationTabs
        tabList={
          manageUserStore.isSubscriptionWorkspace
            ? [UsersOverviewTab, SubscriptionUserPurchasesTab]
            : [UsersOverviewTab, UsersAssignmentsTab, UsersTopicsTab, UsersQuestionsTab]
        }
        id={id}
      />
      {showEdit && (
        <EditUser
          open={showEdit}
          onClose={() => setShowEdit(false)}
          onUpdate={(form) => {
            const user_status =
              overviewStats.deactivation_datetime !== null ? 'Deactivated' : 'Active'
            if (user_status !== form.user_status) {
              confirm({
                danger: true,
                title: user_status === 'Active' ? 'Deactivate Users' : 'Reactivate Users',
                confirmationMessage: `${selectedUserProfile.first_name} ${selectedUserProfile.last_name} has been ${user_status === 'Active' ? 'deactivated' : 'reactivated'}.`,
                dialogText: `Are you sure you want to ${user_status === 'Active' ? 'deactivate' : 'reactivate'} ${selectedUserProfile.first_name} ${selectedUserProfile.last_name}?`,
              })
                .then(() => {
                  changeStatus(user_status === 'Active' ? 'deactivate' : 'activate', [
                    selectedUserProfile.id,
                  ]).then(() => {
                    setTimeout(() => {
                      getUserOverviewStats(start, end, match?.params.id)
                    }, 400)
                  })
                })
                .catch(() => {})
            }

            getSelectedUserProfile()
            setShowUpdateDialog(true)
          }}
          isOwner={overviewStats?.roles?.some((r) => r.name === 'Owner')}
          onDelete={() => setShowConfirmDelete(true)}
        />
      )}
      <Dialog
        open={showUpdateDialog}
        title="User Updated"
        dialogText="This user's information has been updated."
        onConfirm={() => {
          setShowEdit(false)
          setShowUpdateDialog(false)
        }}
        confirmBtnLabel="Close"
        maxWidth="xs"
      />
      <Dialog
        danger
        title={`Delete ${selectedUserProfile?.first_name} ${selectedUserProfile?.last_name}`}
        maxWidth="xs"
        dialogText="Are you sure you want to delete this user?"
        onConfirm={handleDelete}
        onClose={() => {
          setShowConfirmDelete(false)
        }}
        onCancel={() => {
          setShowConfirmDelete(false)
        }}
        open={showConfirmDelete}
      />
    </>
  )
})
